// Sport-aware evaluation criteria.
// Structure: SPORTS[sportId].positions[groupId] -> array of category objects.
// A "group" bundles positions that share the same rubric (e.g. Forward + Defense = Skater).
// Adding a new sport later = new sport file / entry here.  Nothing else needs to change.

// -----------------------------------------------------------------------------
// HOCKEY  Skater rubric (Forwards & Defense share this framework)
// -----------------------------------------------------------------------------
const HOCKEY_SKATER = [
  {
    id: 'skating', name: 'Skating Elements', short: 'Skating', icon: 'Zap',
    items: [
      'Speed - first step acceleration, change of pace, ability to break',
      'Agility - deceptiveness, pivots, footwork or foot speed',
      'Balance - stops, starts, pivots (all directions), edges',
      'Power - strength in relation to stride efficiency / battle balance',
      'Forward - speed, small space agility, open ice power',
      'Defense - lateral agility, transition skating, backwards power',
    ],
  },
  {
    id: 'offensive_skills', name: 'Offensive Individual Skills', short: 'Off. Skills', icon: 'Target',
    items: [
      'Patience and poise',
      'Puck protection (tight/wide, body, hands)',
      'Puck handling + skating creativity, control, deception',
      'Passing skills - accuracy, variety, reception',
      'Hand-eye coordination',
      'Smart around the net / consistent scorer',
      'Playmaking ability',
      '1-on-1 challenges - risk vs reward',
      'Willingness to take puck to the net',
      'Shooting - velocity, accuracy, quick release, variety',
      'Creates scoring chances as shooting or passing threat',
      'Decision making - pass vs shoot vs carry',
    ],
  },
  {
    id: 'defensive_skills', name: 'Defensive Individual Skills', short: 'Def. Skills', icon: 'Shield',
    items: [
      'Angling, steering, pressure and contain',
      'Cutting the ice in half (forecheck)',
      'Support and tracking behind the puck (DZC)',
      'Legal, disciplined contact as F1',
      'Reading & reacting without the puck',
      'Controlling offensive stick / body',
      'Protecting the middle',
      'Stick positioning - open ice & small ice',
      'Finding shooting lane / shot blocking',
      'Recovery in battle and checking situations',
    ],
  },
  {
    id: 'speed', name: 'Plays With Speed', short: 'Speed', icon: 'Gauge',
    items: [
      'Controls the pace of the game',
      'Plays with speed, tempo, pace',
      'Challenges opponents with speed',
      'Maintains/increases speed with the puck',
      'Feet moving with puck, evasive high-pace plays',
      'Initiative - F1 forecheck, backcheck, puck races',
    ],
  },
  {
    id: 'off_team', name: 'Offensive Team Play', short: 'Off. Team', icon: 'Users',
    items: [
      'Sees opportunities & creates chances for team',
      'Scores off the rush without being in a rush',
      'Spreads defense / pressures with or without puck',
      'Timing and support on transitions & breakouts',
      'Timing and support on offensive attack',
      'Contributes possession/chances off faceoffs',
      'Defense - part of 4 point of attack',
      'Positive, timely, effective communication',
    ],
  },
  {
    id: 'def_team', name: 'Defensive Team Play', short: 'Def. Team', icon: 'ShieldCheck',
    items: [
      'Protects the middle in all circumstances',
      'Positional awareness',
      'Support of teammates positioning',
      'Recovery in battle and checking situations',
      'Positive, timely, effective communication',
    ],
  },
  {
    id: 'hockey_sense', name: 'Hockey Sense', short: 'Sense', icon: 'Brain',
    items: [
      'Reads plays / anticipates attacks',
      'Understanding of faceoff alignments',
      'Head up, trusts instincts',
      'Game skate - timing & support both ways',
      'Uses change of speed skating',
      'Decision making makes teammates better',
      'Balances risk vs reward / reliable in key moments',
      'Poise and patience under pressure',
      'Executes the little things (chips, picks, lane races)',
      'Adjusts play based on score, time, situation',
      'Executes team style/game plan consistently',
    ],
  },
  {
    id: 'strength', name: 'Strength / Power', short: 'Strength', icon: 'Dumbbell',
    items: [
      'Uses size appropriately in battle situations',
      'Physically challenges the opponent',
      'Engages in battles / maintains compete level',
      'Threat with shot - velocity and quick release',
      'Balance & stability on skates',
      'Consistent battle level with or without puck',
      'Mental toughness / willing to engage physically',
      'Attacks bigger defenders without compromise',
    ],
  },
  {
    id: 'discipline', name: 'Discipline', short: 'Discipline', icon: 'Scale',
    items: [
      'Plays within the rules',
      'Hard nosed with class and professionalism',
      'Pays the price to score / prevent chances',
      'Within all team systems',
      'Shift management',
      'Hard to play against, not at team expense',
      'Keeps body language in check',
      'Understands role based on score & time',
    ],
  },
  {
    id: 'work_ethic', name: 'Work Ethic', short: 'Work', icon: 'Flame',
    items: [
      'Leadership - consistency of effort',
      'Sets pace in games and practices',
      'Changes momentum with work ethic',
      'Battles / competes for loose pucks',
      'Positioning in scoring areas',
      'Improves at practice & off-ice training',
      'Intensity, courage, plays tough within rules',
      'Fights through checks',
      'Willingness to do little things (blocks, chips)',
      'Mental toughness through bad breaks',
    ],
  },
];

// -----------------------------------------------------------------------------
// HOCKEY  Goalie rubric (position-specific framework)
// -----------------------------------------------------------------------------
const HOCKEY_GOALIE = [
  {
    id: 'g_skating', name: 'Skating & Movement', short: 'Movement', icon: 'Zap',
    items: [
      'T-push efficiency and power',
      'Shuffle - balance, seal, quick reset',
      'Butterfly slides - explosive, controlled, square on arrival',
      'C-cuts and edge control in tight',
      'Recoveries - up-time from butterfly to feet',
      'Backwards / forwards mobility on angle changes',
    ],
  },
  {
    id: 'g_positioning', name: 'Positioning & Angles', short: 'Angles', icon: 'Target',
    items: [
      'Depth relative to puck location and threat',
      'Squareness to the puck / shoulder alignment',
      'Net awareness - finds posts without looking',
      'Angle adjustments during puck movement',
      'Reads play development before the shot',
      'Manages high-danger areas / inside-out play',
    ],
  },
  {
    id: 'g_save_selection', name: 'Save Selection', short: 'Selection', icon: 'Brain',
    items: [
      'Reads shot type and selects the right technique',
      'Stand-up vs butterfly decisions',
      'Reactive vs pre-committed  patience shown',
      'Handles in-tight plays with correct technique',
      'Adapts save selection to screens and traffic',
      'Executes on breakaways and shootout attempts',
    ],
  },
  {
    id: 'g_butterfly', name: 'Butterfly & Recovery', short: 'Butterfly', icon: 'Shield',
    items: [
      'Butterfly form - knees down, hips square, chest up',
      'Ice seal - eliminates the five-hole',
      'Butterfly slide push and stop control',
      'Up-time efficiency after a save',
      'Reloads to butterfly on second/third chances',
      'Balance while sealing low posts',
    ],
  },
  {
    id: 'g_hands', name: 'Glove & Blocker', short: 'Hands', icon: 'ShieldCheck',
    items: [
      'Glove hand position and readiness',
      'Catches cleanly and holds for whistles',
      'Blocker redirects to safe zones',
      'Hand movement is quiet - not chasing shots',
      'Overlap of stick and pads on low shots',
      'Glove & blocker consistency on shots to the corners',
    ],
  },
  {
    id: 'g_post_play', name: 'Post Play', short: 'Posts', icon: 'Scale',
    items: [
      'VH (Vertical-Horizontal) execution',
      'RVH (Reverse VH) execution and seal',
      'Timing on post integration',
      'Wraparound coverage - post to post transition',
      'Reads plays behind the net',
      'Backdoor coverage on cross-crease plays',
    ],
  },
  {
    id: 'g_rebound', name: 'Rebound Control', short: 'Rebounds', icon: 'Gauge',
    items: [
      'Directs rebounds to safe / low-danger areas',
      'Absorbs shots in traffic / cover for whistles',
      'Pad-and-stick rebound control on low shots',
      'Body positioning to eliminate second chances',
      'Limits juicy rebounds off the chest',
      'Consistent rebound control under pressure',
    ],
  },
  {
    id: 'g_tracking', name: 'Puck Tracking & Vision', short: 'Tracking', icon: 'Users',
    items: [
      'Tracks puck cleanly from stick to body',
      'Sees through screens and traffic',
      'Reads deflections and tips',
      'Head on a swivel behind the net',
      'Follows the puck on rebounds & second chances',
      'Anticipation on cross-ice passes',
    ],
  },
  {
    id: 'g_handling', name: 'Puck Handling & Retrievals', short: 'Handling', icon: 'Dumbbell',
    items: [
      'Rim / dump retrievals - reads pressure early',
      'Sets D up with clean, catchable passes',
      'Decision making - hold, rim, or play',
      'Stops behind the net cleanly',
      'Bumps and dishes under forecheck',
      'Handles pucks on the PK with composure',
    ],
  },
  {
    id: 'g_compete', name: 'Compete, Composure & Communication', short: 'Compete', icon: 'Flame',
    items: [
      'Battle level in tight / scramble situations',
      'Mental toughness after a goal against',
      'Consistent focus through full 60 minutes',
      'Communication with D - screens, back door, change',
      'Loud, short, clear calls in DZ coverage',
      'Body language and confidence with team',
      'Preparation and practice habits',
    ],
  },
];

// -----------------------------------------------------------------------------
// Sport registry  add sports here as the platform expands.
// -----------------------------------------------------------------------------
export const SPORTS = {
  hockey: {
    id: 'hockey',
    label: 'Hockey',
    positions: [
      { id: 'Forward', label: 'Forward', group: 'skater' },
      { id: 'Defense', label: 'Defense', group: 'skater' },
      { id: 'Goalie',  label: 'Goalie',  group: 'goalie' },
    ],
    rubrics: {
      skater: HOCKEY_SKATER,
      goalie: HOCKEY_GOALIE,
    },
  },
  // Future sports slot in here: soccer, basketball, lacrosse, etc.
};

const DEFAULT_SPORT = 'hockey';

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------
export function getRubricGroup(position, sportId = DEFAULT_SPORT) {
  const sport = SPORTS[sportId] || SPORTS[DEFAULT_SPORT];
  const p = sport.positions.find(x => x.id === position);
  return p ? p.group : 'skater';
}

export function getCategoriesForPosition(position, sportId = DEFAULT_SPORT) {
  const sport = SPORTS[sportId] || SPORTS[DEFAULT_SPORT];
  const group = getRubricGroup(position, sportId);
  return sport.rubrics[group] || sport.rubrics.skater;
}

// Back-compat: existing components importing EVALUATION_CATEGORIES get the
// default skater rubric until we thread `position` through.
export const EVALUATION_CATEGORIES = HOCKEY_SKATER;

export const RATING_LABELS = {
  1: 'Poor', 2: 'Poor', 3: 'Below Avg', 4: 'Below Avg',
  5: 'Average', 6: 'Above Avg', 7: 'Above Avg',
  8: 'Strong', 9: 'Excellent', 10: 'Elite',
};

export const getCategoryScore = (ratings, categoryId, categories = EVALUATION_CATEGORIES) => {
  const cat = categories.find(c => c.id === categoryId);
  if (!cat) return 0;
  const scores = cat.items
    .map((_, i) => ratings?.[categoryId]?.[i] || ratings?.[categoryId]?.[String(i)] || 0)
    .filter(s => s > 0);
  if (scores.length === 0) return 0;
  return (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1);
};

export const getOverallScore = (ratings, categories = EVALUATION_CATEGORIES) => {
  const catScores = categories
    .map(c => parseFloat(getCategoryScore(ratings, c.id, categories)))
    .filter(s => s > 0);
  if (catScores.length === 0) return 0;
  return (catScores.reduce((a, b) => a + b, 0) / catScores.length).toFixed(1);
};

// Convenience: same helpers but auto-select the rubric from a player position.
export const scoreForPlayer = (ratings, position, sportId = DEFAULT_SPORT) => {
  const categories = getCategoriesForPosition(position, sportId);
  return {
    overall: getOverallScore(ratings, categories),
    byCategory: (id) => getCategoryScore(ratings, id, categories),
    categories,
  };
};
